import { useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft, Pencil, Send, Download, XCircle, Clock, CheckCircle, AlertCircle,
  User, Mail, RefreshCw, Link2, Copy, History, FileText, PenTool
} from 'lucide-react'
import { contractsApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ContractStatus, SignerStatus } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const statusColors: Record<ContractStatus, string> = {
  DRAFT: 'secondary',
  PENDING_SIGNATURE: 'warning',
  PARTIALLY_SIGNED: 'warning',
  SIGNED: 'success',
  EXPIRED: 'destructive',
  CANCELLED: 'destructive',
}

const statusLabels: Record<ContractStatus, string> = {
  DRAFT: 'Draft',
  PENDING_SIGNATURE: 'Pending Signature',
  PARTIALLY_SIGNED: 'Partially Signed',
  SIGNED: 'Signed',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
}

const signerStatusIcons: Record<SignerStatus, any> = {
  PENDING: Clock,
  VIEWED: AlertCircle,
  SIGNED: CheckCircle,
  DECLINED: XCircle,
}

// Signer colors for signature markers
const signerColors = [
  { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', badge: '#1d4ed8' },
  { bg: 'rgba(16, 185, 129, 0.1)', border: '#10b981', badge: '#047857' },
  { bg: 'rgba(245, 158, 11, 0.1)', border: '#f59e0b', badge: '#b45309' },
  { bg: 'rgba(239, 68, 68, 0.1)', border: '#ef4444', badge: '#b91c1c' },
  { bg: 'rgba(139, 92, 246, 0.1)', border: '#8b5cf6', badge: '#6d28d9' },
]

const getSignerColor = (index: number) => signerColors[index % signerColors.length]

export default function ContractDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [signingLinkDialog, setSigningLinkDialog] = useState<{ open: boolean; link: string; signerName: string }>({
    open: false,
    link: '',
    signerName: '',
  })

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.getById(id!),
    enabled: !!id,
  })

  const { data: versions = [] } = useQuery({
    queryKey: ['contractVersions', id],
    queryFn: () => contractsApi.getVersions(id!),
    enabled: !!id,
  })

  const { data: auditLog = [] } = useQuery({
    queryKey: ['contractAudit', id],
    queryFn: () => contractsApi.getAuditLog(id!),
    enabled: !!id,
  })

  // Process contract content - just return the content as-is
  // Signature placements are now PDF page-based and rendered separately
  const processedContent = useMemo(() => {
    if (!contract) return ''
    return contract.content
  }, [contract])

  const cancelMutation = useMutation({
    mutationFn: () => contractsApi.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] })
      toast.success('Contract cancelled')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to cancel contract'))
    },
  })

  const resendMutation = useMutation({
    mutationFn: (signerId: string) => contractsApi.resendSigningLink(id!, signerId),
    onSuccess: () => {
      toast.success('Signing link resent')
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to resend link'))
    },
  })

  const getSigningLink = async (signerId: string, signerName: string) => {
    try {
      const result = await contractsApi.getSigningLink(id!, signerId)
      setSigningLinkDialog({ open: true, link: result.signing_link, signerName })
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to get signing link')
    }
  }

  const copyLink = () => {
    navigator.clipboard.writeText(signingLinkDialog.link)
    toast.success('Link copied to clipboard')
  }

  const downloadPdf = async () => {
    try {
      const blob = await contractsApi.downloadPdf(id!, true)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${contract?.contract_number || 'contract'}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error('Failed to download PDF')
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (value?: number, currency = 'USD') => {
    if (value === undefined || value === null) return '-'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (!contract) {
    return <div className="text-center py-8">Contract not found</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{contract.title}</h1>
              <Badge variant={statusColors[contract.status] as any}>
                {statusLabels[contract.status]}
              </Badge>
            </div>
            <p className="text-muted-foreground">Contract #{contract.contract_number}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canAddEdit('CONTRACTS') && contract.status === 'DRAFT' && (
            <>
              <Button variant="outline" onClick={() => navigate(`/contracts/${id}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button onClick={() => navigate(`/contracts/${id}/send`)}>
                <Send className="mr-2 h-4 w-4" />
                Send for Signing
              </Button>
            </>
          )}
          <Button variant="outline" onClick={downloadPdf}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          {canAddEdit('CONTRACTS') && contract.status !== 'SIGNED' && contract.status !== 'CANCELLED' && (
            <Button variant="destructive" onClick={() => cancelMutation.mutate()}>
              <XCircle className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="signers">Signers ({contract.signers.length})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Contract Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">Value</p>
                  <p className="font-medium">{formatCurrency(contract.value, contract.currency)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Start Date</p>
                  <p className="font-medium">{formatDate(contract.start_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">End Date</p>
                  <p className="font-medium">{formatDate(contract.end_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Renewal Date</p>
                  <p className="font-medium">{formatDate(contract.renewal_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(contract.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sent At</p>
                  <p className="font-medium">{formatDate(contract.sent_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content">
          <Card>
            <CardHeader>
              <CardTitle>Contract Content</CardTitle>
            </CardHeader>
            <CardContent>
              {/* A4 Document Container */}
              <div className="max-h-[600px] overflow-auto border rounded-lg bg-muted/30 p-4">
                <div
                  className="relative bg-white shadow-md mx-auto"
                  style={{
                    width: '210mm',
                    minHeight: '297mm',
                    padding: '20mm',
                    boxSizing: 'border-box',
                  }}
                >
                  {/* Document Content */}
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: processedContent }} />

                  {/* Signature Placement Markers */}
                  {contract.signature_placements?.map((placement, idx) => {
                    const signerIndex = placement.signer_index
                    const signer = contract.signers[signerIndex]
                    const colors = getSignerColor(signerIndex)
                    const isSigned = signer?.status === 'SIGNED'
                    return (
                      <div
                        key={`placement-${idx}`}
                        className="absolute flex items-center gap-1 px-2 py-1 rounded border-2 text-xs font-medium shadow-sm"
                        style={{
                          left: `${placement.x_percent}%`,
                          top: `${placement.y_percent}%`,
                          transform: 'translate(-50%, -50%)',
                          borderColor: isSigned ? '#10b981' : colors.border,
                          backgroundColor: isSigned ? 'rgba(16, 185, 129, 0.1)' : colors.bg,
                          color: isSigned ? '#047857' : colors.badge,
                          zIndex: 10,
                        }}
                      >
                        {isSigned ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <PenTool className="h-3 w-3" />
                        )}
                        <span>{signer?.name || `Signer ${signerIndex + 1}`}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="signers">
          <Card>
            <CardHeader>
              <CardTitle>Signers</CardTitle>
              <CardDescription>People who need to sign this contract</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {contract.signers.map((signer) => {
                // When contract is cancelled, show cancelled status for unsigned signers
                const isCancelled = contract.status === 'CANCELLED' && signer.status !== 'SIGNED'
                const displayStatus = isCancelled ? 'CANCELLED' : signer.status
                const StatusIcon = isCancelled ? XCircle : signerStatusIcons[signer.status]
                const statusColor = signer.status === 'SIGNED' ? 'text-green-500' :
                                   (signer.status === 'DECLINED' || isCancelled) ? 'text-red-500' : 'text-yellow-500'
                return (
                  <div key={signer.signer_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium">{signer.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {signer.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">{signer.signer_type}</Badge>
                      <div className="flex items-center gap-1">
                        <StatusIcon className={`h-4 w-4 ${statusColor}`} />
                        <span className="text-sm">{displayStatus}</span>
                      </div>
                      {signer.status === 'PENDING' && contract.status !== 'CANCELLED' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => getSigningLink(signer.signer_id, signer.name)}>
                            <Link2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => resendMutation.mutate(signer.signer_id)}>
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Version History</CardTitle>
            </CardHeader>
            <CardContent>
              {versions.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No version history</p>
              ) : (
                <div className="space-y-4">
                  {versions.map((version) => (
                    <div key={version.version_id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <History className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="font-medium">Version {version.version_number}</p>
                        <p className="text-sm text-muted-foreground">{version.change_summary || 'No summary'}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">{formatDate(version.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
              <CardDescription>Complete history of all actions on this contract</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLog.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No audit entries</p>
              ) : (
                <div className="space-y-2">
                  {auditLog.map((entry) => (
                    <div key={entry.audit_id} className="flex items-center gap-4 p-3 border-b last:border-0">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{entry.action}</span>
                          {entry.signer_name && <span className="text-muted-foreground"> by {entry.signer_name}</span>}
                        </p>
                        {entry.ip_address && <p className="text-xs text-muted-foreground">IP: {entry.ip_address}</p>}
                      </div>
                      <p className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={signingLinkDialog.open} onOpenChange={(open) => setSigningLinkDialog({ ...signingLinkDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signing Link for {signingLinkDialog.signerName}</DialogTitle>
            <DialogDescription>Share this link with the signer to allow them to sign the contract.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={signingLinkDialog.link} readOnly className="font-mono text-sm" />
            <Button onClick={copyLink}><Copy className="h-4 w-4" /></Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigningLinkDialog({ ...signingLinkDialog, open: false })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

